/**
 * Courtesy-car (sijaisauto) hooks (PF-120 / #2976).
 *
 * Query keys live here because `queryKeys.ts` is owned by another change set.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, ApiError } from '@/lib/api';
import type { WorkshopOpeningHours } from '@/types';
import { hhmmToMinutes, openTimeForDate } from '@/utils/openingHours';

export const COURTESY_LOAN_STATUSES = ['reserved', 'out', 'returned', 'cancelled'] as const;
export type CourtesyLoanStatus = (typeof COURTESY_LOAN_STATUSES)[number];

export const FUEL_EIGHTHS_MIN = 0;
export const FUEL_EIGHTHS_MAX = 8;

export interface CourtesyVehicle {
  id: string;
  vehicleId: string;
  displayName: string | null;
  isActive: boolean;
  dailyRateCents: number | null;
  notes: string | null;
  registrationNumber: string;
  make: string | null;
  model: string | null;
  year: number | null;
  createdAt: string;
  updatedAt: string;
}

export interface CourtesyLoanConflict {
  loanId: string;
  status: CourtesyLoanStatus;
  plannedStart: string;
  plannedEnd: string;
  bookingId: string | null;
  borrowerPersonId: string | null;
  borrowerCompanyId: string | null;
}

export interface CourtesyVehicleAvailability extends CourtesyVehicle {
  available: boolean;
  conflict: CourtesyLoanConflict | null;
}

export interface CourtesyLoan {
  id: string;
  courtesyVehicleId: string;
  vehicleId: string;
  registrationNumber: string;
  displayName: string | null;
  dailyRateCents: number | null;
  bookingId: string | null;
  borrowerPersonId: string | null;
  borrowerCompanyId: string | null;
  plannedStart: string;
  plannedEnd: string;
  status: CourtesyLoanStatus;
  outAt: string | null;
  outMileage: number | null;
  outFuelEighths: number | null;
  outNotes: string | null;
  returnedAt: string | null;
  returnMileage: number | null;
  returnFuelEighths: number | null;
  returnNotes: string | null;
  priceCents: number | null;
  invoicedItemId: string | null;
  createdBy: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface CreateCourtesyVehicleData {
  registrationNumber: string;
  displayName?: string | null;
  dailyRateCents?: number | null;
  notes?: string | null;
  isActive?: boolean;
}

export interface UpdateCourtesyVehicleData {
  displayName?: string | null;
  dailyRateCents?: number | null;
  notes?: string | null;
  isActive?: boolean;
}

export interface ReserveCourtesyLoanData {
  courtesyVehicleId: string;
  plannedStart: string;
  plannedEnd: string;
  bookingId?: string | null;
  borrowerPersonId?: string | null;
  borrowerCompanyId?: string | null;
}

export interface UpdateCourtesyLoanData {
  plannedStart?: string;
  plannedEnd?: string;
  status?: 'cancelled';
}

export interface CourtesyLoanHandoverData {
  outAt?: string;
  mileage: number;
  fuelEighths: number;
  notes?: string | null;
}

export interface CourtesyLoanReturnData {
  returnedAt?: string;
  mileage: number;
  fuelEighths: number;
  notes?: string | null;
}

export interface CourtesyVehicleFilters {
  q?: string;
  isActive?: boolean;
}

interface PaginatedResponse<T> {
  data: T[];
  pagination: {
    nextCursor: string | null;
    prevCursor: string | null;
    hasMore: boolean;
  };
}

export const courtesyQueryKeys = {
  all: ['courtesyVehicles'] as const,
  lists: () => [...courtesyQueryKeys.all, 'list'] as const,
  list: (filters: CourtesyVehicleFilters) => [...courtesyQueryKeys.lists(), filters] as const,
  details: () => [...courtesyQueryKeys.all, 'detail'] as const,
  detail: (id: string) => [...courtesyQueryKeys.details(), id] as const,
  availability: (from: string, to: string) =>
    [...courtesyQueryKeys.all, 'availability', { from, to }] as const,
  loansAll: ['courtesyLoans'] as const,
  loansByWorkOrder: (workOrderId: string) =>
    [...courtesyQueryKeys.loansAll, 'workOrder', workOrderId] as const,
};

function listParams(filters: CourtesyVehicleFilters): Record<string, string | number | boolean> {
  const params: Record<string, string | number | boolean> = { limit: 100 };
  if (filters.q) params.q = filters.q;
  if (filters.isActive !== undefined) params.isActive = filters.isActive;
  return params;
}

function invalidateCourtesyQueries(queryClient: ReturnType<typeof useQueryClient>): void {
  void queryClient.invalidateQueries({ queryKey: courtesyQueryKeys.all });
  void queryClient.invalidateQueries({ queryKey: courtesyQueryKeys.loansAll });
}

export function fuelEighthsToPercent(eighths: number): number {
  return (eighths / FUEL_EIGHTHS_MAX) * 100;
}

function parseInstant(value: string | null | undefined): Date | null {
  if (!value) return null;
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
}

/**
 * Clock start of the booked work. A floating ("during the day") booking is
 * stored at UTC midnight of its civil date — a day marker, not a time — so
 * the loan starts at that day's opening time instead (#4094).
 */
function scheduledWorkStart(
  scheduledAt: Date,
  schedulingType: 'exact' | 'floating' | undefined,
  businessHours: WorkshopOpeningHours | null | undefined,
): Date {
  const isDayMarker =
    schedulingType === 'floating' ||
    (scheduledAt.getUTCHours() === 0 &&
      scheduledAt.getUTCMinutes() === 0 &&
      scheduledAt.getUTCSeconds() === 0);
  if (!isDayMarker) return scheduledAt;
  const civilDate = scheduledAt.toISOString().slice(0, 10);
  const minutes = hhmmToMinutes(openTimeForDate(civilDate, businessHours)) ?? 8 * 60;
  const [year, month, day] = civilDate.split('-').map(Number);
  return new Date(year ?? 1970, (month ?? 1) - 1, day ?? 1, Math.floor(minutes / 60), minutes % 60);
}

/**
 * Default courtesy-car loan window for a work order (#4094).
 *
 * Start: the vehicle drop-off time, else the scheduled time (opening time of
 * the day for floating work — never midnight). End: the customer's requested
 * pickup when it is after the start, else start + estimated duration (1 h
 * when unknown) + 1 h.
 */
export function defaultCourtesyLoanRange(
  workOrder: {
    scheduledAt: string;
    estimatedDurationHours: number | null;
    schedulingType?: 'exact' | 'floating' | undefined;
    vehicleDropoffAt?: string | null | undefined;
    customerRequestedPickupAt?: string | null | undefined;
  },
  businessHours?: WorkshopOpeningHours | null,
): { plannedStart: string; plannedEnd: string } {
  const scheduledAt = new Date(workOrder.scheduledAt);
  const start =
    parseInstant(workOrder.vehicleDropoffAt) ??
    scheduledWorkStart(scheduledAt, workOrder.schedulingType, businessHours);
  const pickup = parseInstant(workOrder.customerRequestedPickupAt);
  const durationHours = workOrder.estimatedDurationHours ?? 1;
  const end =
    pickup && pickup.getTime() > start.getTime()
      ? pickup
      : new Date(start.getTime() + (durationHours + 1) * 60 * 60 * 1000);
  return { plannedStart: start.toISOString(), plannedEnd: end.toISOString() };
}

export function courtesyLoanFromConflict(
  vehicle: CourtesyVehicleAvailability,
): CourtesyLoan | null {
  if (!vehicle.conflict) return null;
  const conflict = vehicle.conflict;
  return {
    id: conflict.loanId,
    courtesyVehicleId: vehicle.id,
    vehicleId: vehicle.vehicleId,
    registrationNumber: vehicle.registrationNumber,
    displayName: vehicle.displayName,
    dailyRateCents: vehicle.dailyRateCents,
    bookingId: conflict.bookingId,
    borrowerPersonId: conflict.borrowerPersonId,
    borrowerCompanyId: conflict.borrowerCompanyId,
    plannedStart: conflict.plannedStart,
    plannedEnd: conflict.plannedEnd,
    status: conflict.status,
    outAt: null,
    outMileage: null,
    outFuelEighths: null,
    outNotes: null,
    returnedAt: null,
    returnMileage: null,
    returnFuelEighths: null,
    returnNotes: null,
    priceCents: null,
    invoicedItemId: null,
    createdBy: null,
    createdAt: vehicle.updatedAt,
    updatedAt: vehicle.updatedAt,
  };
}

export function courtesyLoanBarRange(loan: {
  plannedStart: string;
  plannedEnd: string;
  status: CourtesyLoanStatus;
  outAt: string | null;
  returnedAt: string | null;
}): { start: string; end: string } {
  if (loan.status === 'returned' && loan.outAt && loan.returnedAt) {
    return { start: loan.outAt, end: loan.returnedAt };
  }
  if (loan.status === 'out' && loan.outAt) {
    return { start: loan.outAt, end: loan.returnedAt ?? loan.plannedEnd };
  }
  return { start: loan.plannedStart, end: loan.plannedEnd };
}

export function isCourtesyVehicleUnavailableError(error: unknown): boolean {
  return (
    error instanceof ApiError &&
    error.status === 409 &&
    typeof error.data === 'object' &&
    error.data !== null &&
    'code' in error.data &&
    error.data.code === 'courtesy_vehicle_unavailable'
  );
}

export function isCourtesyVehicleAlreadyRegisteredError(error: unknown): boolean {
  return error instanceof ApiError && error.status === 409;
}

export function useCourtesyVehicles(filters: CourtesyVehicleFilters = {}) {
  return useQuery({
    queryKey: courtesyQueryKeys.list(filters),
    queryFn: () =>
      api.get<PaginatedResponse<CourtesyVehicle>>('/courtesy-vehicles', {
        params: listParams(filters),
      }),
    staleTime: 60 * 1000,
    select: (data) => data.data,
  });
}

export function useCourtesyVehicle(id: string | undefined) {
  return useQuery({
    queryKey: courtesyQueryKeys.detail(id ?? ''),
    queryFn: () => api.get<CourtesyVehicle>(`/courtesy-vehicles/${id}`),
    enabled: !!id,
    staleTime: 60 * 1000,
  });
}

export function useCourtesyAvailability(from: string | undefined, to: string | undefined) {
  return useQuery({
    queryKey: courtesyQueryKeys.availability(from ?? '', to ?? ''),
    queryFn: () =>
      api.get<{ data: CourtesyVehicleAvailability[] }>('/courtesy-vehicles/availability', {
        params: { from, to },
      }),
    enabled: !!from && !!to,
    staleTime: 30 * 1000,
    select: (data) => data.data,
  });
}

export function useWorkOrderCourtesyLoans(workOrderId: string | undefined) {
  return useQuery({
    queryKey: courtesyQueryKeys.loansByWorkOrder(workOrderId ?? ''),
    queryFn: () =>
      api.get<{ data: CourtesyLoan[] }>(`/work-orders/${workOrderId}/courtesy-loans`),
    enabled: !!workOrderId,
    staleTime: 30 * 1000,
    select: (data) => data.data,
  });
}

export function useCreateCourtesyVehicle() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: CreateCourtesyVehicleData) =>
      api.post<CourtesyVehicle>('/courtesy-vehicles', data),
    onSuccess: () => {
      invalidateCourtesyQueries(queryClient);
    },
  });
}

export function useUpdateCourtesyVehicle() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateCourtesyVehicleData }) =>
      api.patch<CourtesyVehicle>(`/courtesy-vehicles/${id}`, data),
    onSuccess: (_, { id }) => {
      void queryClient.invalidateQueries({ queryKey: courtesyQueryKeys.detail(id) });
      invalidateCourtesyQueries(queryClient);
    },
  });
}

export function useReserveCourtesyLoan() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: ReserveCourtesyLoanData) =>
      api.post<CourtesyLoan>('/courtesy-loans', data),
    onSuccess: (loan) => {
      invalidateCourtesyQueries(queryClient);
      if (loan.bookingId) {
        void queryClient.invalidateQueries({
          queryKey: courtesyQueryKeys.loansByWorkOrder(loan.bookingId),
        });
      }
    },
  });
}

export function useUpdateCourtesyLoan() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateCourtesyLoanData }) =>
      api.patch<CourtesyLoan>(`/courtesy-loans/${id}`, data),
    onSuccess: (loan) => {
      invalidateCourtesyQueries(queryClient);
      if (loan.bookingId) {
        void queryClient.invalidateQueries({
          queryKey: courtesyQueryKeys.loansByWorkOrder(loan.bookingId),
        });
      }
    },
  });
}

export function useHandoverCourtesyLoan() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: CourtesyLoanHandoverData }) =>
      api.post<CourtesyLoan>(`/courtesy-loans/${id}/handover`, data, {
        headers: { 'Idempotency-Key': crypto.randomUUID() },
      }),
    onSuccess: (loan) => {
      invalidateCourtesyQueries(queryClient);
      if (loan.bookingId) {
        void queryClient.invalidateQueries({
          queryKey: courtesyQueryKeys.loansByWorkOrder(loan.bookingId),
        });
      }
    },
  });
}

export function useReturnCourtesyLoan() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: CourtesyLoanReturnData }) =>
      api.post<CourtesyLoan>(`/courtesy-loans/${id}/return`, data, {
        headers: { 'Idempotency-Key': crypto.randomUUID() },
      }),
    onSuccess: (loan) => {
      invalidateCourtesyQueries(queryClient);
      if (loan.bookingId) {
        void queryClient.invalidateQueries({
          queryKey: courtesyQueryKeys.loansByWorkOrder(loan.bookingId),
        });
      }
    },
  });
}

export async function openCourtesyLoanPdf(loanId: string): Promise<void> {
  const popup = window.open('', '_blank');
  try {
    const blob = await api.getBlob(`/courtesy-loans/${loanId}/pdf`);
    const objectUrl = URL.createObjectURL(blob);
    if (popup) {
      popup.location.href = objectUrl;
    } else {
      const link = document.createElement('a');
      link.href = objectUrl;
      link.target = '_blank';
      link.rel = 'noopener noreferrer';
      link.click();
    }
    window.setTimeout(() => URL.revokeObjectURL(objectUrl), 60_000);
  } catch (error) {
    popup?.close();
    throw error;
  }
}
